import {
    SearchFormMetaType,
    useAutocompleteSearchForm,
    useAutocompleteSearchFormMeta,
} from 'components/Layout/Header/HeaderBottom/Search/formMeta';
import { SEARCH_QUERY_PARAMETER_NAME } from 'config/constants';
import { useAutocompleteSearch } from 'connectors/search/AutocompleteSearch';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useCurrentSearchStringQuery } from 'helpers/queryParams/useCurrentSearchStringQuery';
import useDebounce from 'hooks/helpers/UseDebounce';
import { useLatest } from 'hooks/ui/useLatest';
import { useRouter } from 'next/router';
import { ComponentType, useEffect, useState } from 'react';
import { SubmitHandler, UseFormReturn, useWatch } from 'react-hook-form';
import { AutocompleteSearchFormType } from 'types/form';
import { AutocompleteSearchType } from 'types/search';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

export type WithAutocompleteSearchProps = {
    onAutocompleteSearchSubmitCallback: SubmitHandler<AutocompleteSearchFormType>;
    autocompleteSearchResults: AutocompleteSearchType | undefined;
    autocompleteSearchQueryValue: string;
    formProviderMethods: UseFormReturn<AutocompleteSearchFormType>;
    formMeta: SearchFormMetaType;
    fetching: boolean;
};

const withAutocompleteSearch = (Component: ComponentType<WithAutocompleteSearchProps>): FC => {
    const HOC = () => {
        const router = useRouter();
        const currentSearchString = useCurrentSearchStringQuery() ?? '';
        const [formProviderMethods] = useAutocompleteSearchForm(currentSearchString);
        const formMeta = useAutocompleteSearchFormMeta(formProviderMethods);
        const autocompleteSearchQueryValue = useWatch({
            name: formMeta.fields.autocompleteSearchQuery.name,
            control: formProviderMethods.control,
        });
        const debouncedAutocompleteSearchQuery = useDebounce(autocompleteSearchQueryValue, 200);
        const [autocompleteSearchApiResults, fetching] = useAutocompleteSearch(debouncedAutocompleteSearchQuery);
        const [autocompleteSearchResults, setAutocompleteSearchResults] = useState<AutocompleteSearchType | undefined>(
            undefined,
        );
        const { url } = useDomainConfig();
        const [searchUrl] = getInternationalizedStaticUrls(['/search'], url);
        const autocompleteResultDiff = JSON.stringify(autocompleteSearchApiResults);
        const latestIsValid = useLatest(formProviderMethods.formState.isValid);

        const onAutocompleteSearchSubmitHandler: SubmitHandler<AutocompleteSearchFormType> = (_data, event) => {
            event?.preventDefault();
            router.push({
                pathname: searchUrl,
                query: { [SEARCH_QUERY_PARAMETER_NAME]: autocompleteSearchQueryValue },
            });
        };

        useEffect(() => {
            formProviderMethods.reset({ autocompleteSearchQuery: currentSearchString });
        }, [currentSearchString, formProviderMethods]);

        useEffect(() => {
            if (latestIsValid.current) {
                setAutocompleteSearchResults(autocompleteSearchApiResults);
            } else {
                setAutocompleteSearchResults(undefined);
            }
        }, [autocompleteResultDiff, autocompleteSearchQueryValue, latestIsValid]);

        return (
            <Component
                autocompleteSearchQueryValue={autocompleteSearchQueryValue}
                autocompleteSearchResults={autocompleteSearchResults}
                fetching={fetching}
                formMeta={formMeta}
                formProviderMethods={formProviderMethods}
                onAutocompleteSearchSubmitCallback={onAutocompleteSearchSubmitHandler}
            />
        );
    };

    return HOC;
};

/* @component */
export default withAutocompleteSearch;
